import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiPhone, FiMail, FiInstagram, FiFacebook, FiMessageCircle, FiNavigation } = FiIcons;

const Location = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const proximityPoints = [
    { name: "Udawalawe National Park", distance: "15 minutes", icon: FiNavigation },
    { name: "Elephant Transit Home", distance: "10 minutes", icon: FiMapPin },
    { name: "Udawalawe Reservoir", distance: "8 minutes", icon: FiMapPin },
    { name: "Local Village", distance: "5 minutes", icon: FiMapPin }
  ];

  const contactInfo = [
    {
      icon: FiMapPin,
      label: "Address",
      value: "232, Dahaiyagala, Udawalawe",
      action: "https://maps.google.com/?q=232+Dahaiyagala+Udawalawe"
    },
    {
      icon: FiPhone,
      label: "WhatsApp",
      value: "+94 77 123 4567",
      action: "https://wa.me/94771234567"
    },
    {
      icon: FiMail,
      label: "Email",
      value: "info@walaweparkview.com",
      action: "mailto:info@walaweparkview.com"
    }
  ];

  return (
    <section id="location" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Find <span className="gradient-text">Us</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Perfectly positioned for your Udawalawe adventure. We're just minutes away from 
            the national park entrance and all major attractions.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Map & Proximity */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Interactive Map */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="glass-card p-4 rounded-3xl"
            >
              <div className="w-full h-80 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center relative overflow-hidden">
                {/* Map Placeholder with Animation */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={inView ? { scale: 1, opacity: 1 } : {}}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="text-center"
                >
                  <SafeIcon icon={FiMapPin} className="text-6xl gradient-text mb-4" />
                  <p className="text-white font-semibold mb-2">Interactive Map</p>
                  <p className="text-white/60 text-sm">Click to open in Google Maps</p>
                </motion.div>
                
                {/* Animated Markers */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                >
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                </motion.div>
              </div>
              
              <motion.a
                href="https://maps.google.com/?q=232+Dahaiyagala+Udawalawe"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                className="block mt-4 glass-morphism py-3 rounded-xl text-white text-center font-medium hover:bg-white/20 transition-all duration-300"
              >
                Open in Google Maps
              </motion.a>
            </motion.div>

            {/* Proximity Points */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.4 }}
              className="space-y-4"
            >
              <h3 className="text-2xl font-bold text-white mb-6">
                <SafeIcon icon={FiClock} className="inline mr-3 gradient-text" />
                Nearby Attractions
              </h3>
              
              {proximityPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.02, x: 10 }}
                  className="glass-card p-4 rounded-2xl flex items-center space-x-4"
                >
                  <div className="p-3 rounded-xl bg-gradient-to-r from-blue-400 to-purple-500">
                    <SafeIcon icon={point.icon} className="text-white text-lg" />
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">{point.name}</h4>
                    <p className="text-white/60 text-sm">{point.distance} away</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Contact & Social */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.3 }}
            className="space-y-8"
          >
            {/* Contact Information */}
            <div className="glass-morphism p-8 rounded-3xl">
              <h3 className="text-2xl font-bold text-white mb-8">
                Get in <span className="gradient-text">Touch</span>
              </h3>
              
              <div className="space-y-6">
                {contactInfo.map((contact, index) => (
                  <motion.a
                    key={index}
                    href={contact.action}
                    target={contact.action.startsWith('http') ? '_blank' : '_self'}
                    rel={contact.action.startsWith('http') ? 'noopener noreferrer' : ''}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.05, x: 10 }}
                    className="flex items-center space-x-4 p-4 glass-card rounded-2xl hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="p-3 rounded-xl bg-gradient-to-r from-green-400 to-blue-500">
                      <SafeIcon icon={contact.icon} className="text-white text-lg" />
                    </div>
                    
                    <div>
                      <h4 className="text-white font-semibold">{contact.label}</h4>
                      <p className="text-white/70">{contact.value}</p>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Social Media */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-2xl font-bold text-white mb-6">
                Follow Our <span className="gradient-text">Adventures</span>
              </h3>
              
              <div className="flex space-x-4 mb-6">
                <motion.a
                  href="https://instagram.com/walaweparkview"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="p-4 glass-morphism rounded-2xl hover:bg-white/20 transition-all duration-300"
                >
                  <SafeIcon icon={FiInstagram} className="text-2xl text-white" />
                </motion.a>
                
                <motion.a
                  href="https://facebook.com/walaweparkview"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  className="p-4 glass-morphism rounded-2xl hover:bg-white/20 transition-all duration-300"
                >
                  <SafeIcon icon={FiFacebook} className="text-2xl text-white" />
                </motion.a>
              </div>
              
              <p className="text-white/70 text-sm mb-6">
                See daily wildlife sightings and behind-the-scenes moments from our safaris. 
                Follow us for the latest updates and amazing animal encounters!
              </p>
            </motion.div>

            {/* Final CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.8 }}
              className="text-center"
            >
              <motion.a
                href="https://wa.me/94771234567?text=Hi%20Mohan,%20I%20would%20like%20to%20book%20a%20safari%20experience"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-3 glass-morphism px-8 py-4 rounded-full text-white font-semibold text-lg hover:bg-white/20 transition-all duration-300"
              >
                <SafeIcon icon={FiMessageCircle} className="text-xl" />
                <span>Start Your Adventure</span>
              </motion.a>
              
              <p className="text-white/60 text-sm mt-4">
                Ready to explore Udawalawe? Message Mohan directly on WhatsApp
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 1 }}
          className="mt-20 pt-12 border-t border-white/20 text-center"
        >
          <div className="glass-card p-8 rounded-3xl">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="text-3xl font-bold gradient-text mb-4"
            >
              Walawe Park View Hotel
            </motion.div>
            
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Beyond the Stay. The Ultimate Udawalawe Safari Experience.
              <br />
              Creating memories, one safari at a time.
            </p>
            
            <div className="text-white/60 text-sm">
              © 2025 Walawe Park View Hotel. All rights reserved.
              <br />
              Designed and Created by Rajantha
            </div>
          </div>
        </motion.footer>
      </div>
    </section>
  );
};

export default Location;